import React, { useState, useEffect, useCallback } from 'react';
import { useFileUpload } from '../../hooks/useFileUpload';
import { 
  uploadPastPaper, 
  getAllPapers,
  deletePastPaper,
  addSubject,
  getAllSubjects,
  deleteSubject,
  addVideoLesson,
  getAllVideos,
  deleteVideoLesson,
  createExam,
  getAllExams,
  deleteExam,
  updateExam
} from '../../services/admin';
import BulkQuestionUpload from './BulkQuestionUpload';
import BulkPaperUpload from './BulkPaperUpload';
import '../../styles/bulk-upload.css';

// Constants
const SUBJECTS = [
  { value: 'mathematics', label: 'Mathematics' },
  { value: 'english', label: 'English' },
  { value: 'science', label: 'Science' },
  { value: 'social_studies', label: 'Social Studies' },
  { value: 'history', label: 'History' },
  { value: 'geography', label: 'Geography' }
];

const LEVELS = [
  { value: 'grade7', label: 'Grade 7' },
  { value: 'grade12', label: 'Grade 12' }
];

const PAPER_TYPES = [
  { value: 'past_paper', label: 'Past Paper' },
  { value: 'mock_exam', label: 'Mock Exam' },
  { value: 'revision', label: 'Revision Notes' }
];

const TERMS = [
  { value: 'term1', label: 'Term 1' },
  { value: 'term2', label: 'Term 2' },
  { value: 'term3', label: 'Term 3' }
];

// Custom hook for form management
const useFormManagement = (initialState) => {
  const [formData, setFormData] = useState(initialState);
  
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);
  
  const resetForm = useCallback(() => {
    setFormData(initialState);
  }, [initialState]);
  
  return { formData, updateField, resetForm };
};

const ContentManagement = () => {
  // Tab and content state
  const [activeTab, setActiveTab] = useState('papers');
  const [papers, setPapers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [videos, setVideos] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [modals, setModals] = useState({
    upload: false,
    subject: false,
    video: false,
    exam: false,
    editQuestions: false,
    bulkUpload: false,
    bulkQuestions: false
  });
  const [selectedExam, setSelectedExam] = useState(null);
  
  // Question editing state
  const [questionForm, setQuestionForm] = useState({
    question: '',
    type: 'multiple_choice',
    options: ['', '', '', ''],
    correctAnswer: 0,
    marks: 1,
    imageUrl: ''
  });
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  
  // Form states
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Custom hooks
  const { upload, uploading } = useFileUpload();
  const paperForm = useFormManagement({
    title: '',
    description: '',
    subjectId: '',
    levelId: '',
    year: new Date().getFullYear().toString(),
    term: '',
    paperType: 'past_paper'
  });
  
  const subjectForm = useFormManagement({
    title: '',
    description: '',
    levelId: '',
    subjectId: ''
  });
  
  const videoForm = useFormManagement({
    title: '',
    description: '',
    subjectId: '',
    levelId: '',
    videoUrl: '',
    duration: ''
  });
  
  const examForm = useFormManagement({
    title: '',
    description: '',
    subjectId: '',
    levelId: '',
    examDuration: 60,
    passingMarks: 50,
    totalMarks: 100
  });

  // Modal management
  const openModal = useCallback((modalType, exam = null) => {
    if (modalType === 'editQuestions' && exam) {
      setSelectedExam(exam);
    }
    setModals(prev => ({ ...prev, [modalType]: true }));
    setError('');
  }, []);
  
  const closeModal = useCallback((modalType) => {
    setModals(prev => ({ ...prev, [modalType]: false }));
    setError('');
    setSelectedFile(null);
    if (modalType === 'editQuestions') {
      setSelectedExam(null);
    }
  }, []);

  // Handle bulk upload completion
  const handleBulkUploadComplete = useCallback((uploadedCount) => {
    showSuccessNotification(`Successfully uploaded ${uploadedCount} past papers!`);
    // Refresh papers list
    fetchAllContent();
  }, []);

  // Data fetching
  const fetchAllContent = useCallback(async () => {
    setLoading(true);
    try {
      const [papersData, subjectsData, videosData, examsData] = await Promise.all([
        getAllPapers(),
        getAllSubjects(),
        getAllVideos(),
        getAllExams()
      ]);
      
      setPapers(papersData || []);
      setSubjects(subjectsData || []);
      setVideos(videosData || []);
      setExams(examsData || []);
    } catch (error) {
      console.error('Error fetching content:', error);
      showErrorNotification('Failed to load content. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllContent();
  }, [fetchAllContent]);
  
  // Question image handling
  const handleQuestionImageSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showErrorNotification('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showErrorNotification('Image size should be less than 5MB');
        return;
      }
      
      setQuestionForm(prev => ({
        ...prev,
        imageFile: file,
        imageUrl: URL.createObjectURL(file) // Preview URL
      }));
    }
  }, []);
  
  // File handling for PDF upload
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError('');
    }
  }, []);
  
  const handleQuestionImageUpload = async () => {
    if (!questionForm.imageFile) return null;
    
    try {
      const result = await upload(questionForm.imageFile, 'zedscholar/questions');
      return result.url;
    } catch (error) {
      console.error('Error uploading question image:', error);
      showErrorNotification('Failed to upload image');
      return null;
    }
  };
  
  const removeQuestionImage = () => {
    setQuestionForm(prev => ({
      ...prev,
      imageFile: null,
      imageUrl: ''
    }));
  };
  
  // Question management functions
  const resetQuestionForm = () => {
    setQuestionForm({
      question: '',
      type: 'multiple_choice',
      options: ['', '', '', ''],
      correctAnswer: 0,
      marks: 1,
      imageUrl: '',
      imageFile: null
    });
    setEditingQuestionIndex(null);
  };
  
  const handleAddQuestion = async () => {
    if (!questionForm.question.trim()) {
      showErrorNotification('Please enter a question');
      return;
    }
    
    try {
      // Upload image if exists
      let imageUrl = '';
      if (questionForm.imageFile) {
        imageUrl = await handleQuestionImageUpload();
        if (!imageUrl) return; // Upload failed
      }
      
      // Create clean question object
      const newQuestion = {
        id: Date.now(),
        question: questionForm.question.trim(),
        type: questionForm.type,
        marks: parseInt(questionForm.marks) || 1,
        correctAnswer: questionForm.correctAnswer,
        imageUrl: imageUrl || undefined
      };
      
      // Add options only for multiple choice questions
      if (questionForm.type === 'multiple_choice') {
        newQuestion.options = questionForm.options.filter(opt => opt && opt.trim());
      }
      
      // Remove undefined imageFile
      delete newQuestion.imageFile;
      
      console.log('Adding new question:', newQuestion);
      
      // Add question to selected exam
      const updatedExam = {
        ...selectedExam,
        questions: [...(selectedExam.questions || []), newQuestion]
      };
      
      setSelectedExam(updatedExam);
      resetQuestionForm();
      showSuccessNotification('Question added successfully!');
    } catch (error) {
      console.error('Error adding question:', error);
      showErrorNotification('Failed to add question');
    }
  };
  
  // Delete question from exam
  const handleDeleteQuestion = (questionIndex) => {
    if (!selectedExam || !selectedExam.questions) return;
    
    const updatedQuestions = selectedExam.questions.filter((_, index) => index !== questionIndex);
    const updatedExam = {
      ...selectedExam,
      questions: updatedQuestions
    };
    
    setSelectedExam(updatedExam);
    showSuccessNotification('Question deleted successfully!');
  };

  // Edit question handler
  const handleEditQuestion = (questionIndex) => {
    const question = selectedExam.questions[questionIndex];
    if (!question) return;
    
    setQuestionForm({
      question: question.question || '',
      type: question.type || 'multiple_choice',
      options: question.options || ['', '', '', ''],
      correctAnswer: question.correctAnswer || 0,
      marks: question.marks || 1,
      imageUrl: question.imageUrl || '',
      imageFile: null
    });
    setEditingQuestionIndex(questionIndex);
  };

  // Save edited question
  const handleSaveEditedQuestion = async () => {
    if (!questionForm.question.trim()) {
      showErrorNotification('Please enter a question');
      return;
    }
    
    if (editingQuestionIndex === null || !selectedExam) return;
    
    try {
      // Upload image if exists
      let imageUrl = questionForm.imageUrl;
      if (questionForm.imageFile) {
        imageUrl = await handleQuestionImageUpload();
        if (!imageUrl) return; // Upload failed
      }
      
      // Create updated question object
      const updatedQuestion = {
        id: selectedExam.questions[editingQuestionIndex].id || Date.now(),
        question: questionForm.question.trim(),
        type: questionForm.type,
        marks: parseInt(questionForm.marks) || 1,
        correctAnswer: questionForm.correctAnswer,
        imageUrl: imageUrl || undefined
      };
      
      // Add options only for multiple choice questions
      if (questionForm.type === 'multiple_choice') {
        updatedQuestion.options = questionForm.options.filter(opt => opt && opt.trim());
      }
      
      // Update questions array
      const updatedQuestions = [...selectedExam.questions];
      updatedQuestions[editingQuestionIndex] = updatedQuestion;
      
      const updatedExam = {
        ...selectedExam,
        questions: updatedQuestions
      };
      
      setSelectedExam(updatedExam);
      resetQuestionForm();
      showSuccessNotification('Question updated successfully!');
    } catch (error) {
      console.error('Error updating question:', error);
      showErrorNotification('Failed to update question');
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    resetQuestionForm();
  };
  
  // Save exam changes to database
  const handleSaveExamChanges = async () => {
    if (!selectedExam) return;
    
    try {
      setSubmitting(true);
      
      // Clean question data to remove undefined values
      const cleanedQuestions = (selectedExam.questions || []).map(question => {
        const cleanedQuestion = {
          id: question.id || Date.now(),
          question: question.question || '',
          type: question.type || 'multiple_choice',
          marks: question.marks || 1,
          correctAnswer: question.correctAnswer
        };
        
        // Add options only for multiple choice
        if (question.type === 'multiple_choice' && question.options) {
          cleanedQuestion.options = question.options.filter(opt => opt !== undefined && opt !== null);
        }
        
        // Add image URL only if it exists
        if (question.imageUrl) {
          cleanedQuestion.imageUrl = question.imageUrl;
        }
        
        return cleanedQuestion;
      });
      
      console.log('Saving cleaned questions:', cleanedQuestions);
      
      // Update exam in database with cleaned questions
      await updateExam(selectedExam.id, {
        questions: cleanedQuestions
      });
      
      // Update local exams state
      const examsData = await getAllExams();
      setExams(examsData);
      
      showSuccessNotification('Exam questions saved successfully!');
      closeModal('editQuestions');
    } catch (error) {
      console.error('Error saving exam changes:', error);
      showErrorNotification('Failed to save exam changes');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle bulk question upload
  const handleBulkQuestionsAdded = async (questions) => {
    if (!selectedExam) {
      showErrorNotification('No exam selected for bulk upload');
      return;
    }
    
    try {
      // Update local state immediately for better UX
      const updatedExam = {
        ...selectedExam,
        questions: [...(selectedExam.questions || []), ...questions]
      };
      setSelectedExam(updatedExam);
      
      // Save to database
      await updateExam(selectedExam.id, updatedExam);
      
      // Update the exams list as well
      setExams(prev => prev.map(exam => 
        exam.id === selectedExam.id ? updatedExam : exam
      ));
      
      showSuccessNotification(`Added ${questions.length} questions successfully!`);
    } catch (error) {
      console.error('Error adding bulk questions:', error);
      showErrorNotification('Failed to add bulk questions: ' + error.message);
      
      // Revert local state on error
      setSelectedExam(selectedExam);
    }
  };
  
  // API handlers with better error handling
  const handleSubmit = async (e, type) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      switch (type) {
        case 'paper':
          if (!selectedFile) {
            setError('Please select a file to upload');
            return;
          }
          
          const uploadResult = await upload(selectedFile);
          const paperData = {
            ...paperForm.formData,
            fileUrl: uploadResult.url,
            fileName: uploadResult.publicId || selectedFile.name,
            fileSize: selectedFile.size
          };
          
          await uploadPastPaper(paperData);
          paperForm.resetForm();
          setSelectedFile(null);
          closeModal('upload');
          
          const papersData = await getAllPapers();
          setPapers(papersData);
          
          showSuccessNotification('Past paper uploaded successfully!');
          break;
          
        case 'subject':
          await addSubject({
            name: subjectForm.formData.title,
            description: subjectForm.formData.description,
            levelId: subjectForm.formData.levelId,
            icon: '📚',
            color: '#4ade80',
            sortOrder: 1 // Added sortOrder field
          });
          
          subjectForm.resetForm();
          closeModal('subject');
          
          const subjectsData = await getAllSubjects();
          setSubjects(subjectsData);
          
          showSuccessNotification('Subject created successfully!');
          break;
          
        case 'video':
          await addVideoLesson({
            ...videoForm.formData,
            thumbnailUrl: '',
            topic: videoForm.formData.title,
            createdAt: new Date()
          });
          
          videoForm.resetForm();
          closeModal('video');
          
          const videosData = await getAllVideos();
          setVideos(videosData);
          
          showSuccessNotification('Video added successfully!');
          break;
          
        case 'exam':
          // Validate required fields
          if (!examForm.formData.title || !examForm.formData.subjectId || !examForm.formData.levelId) {
            showErrorNotification('Please fill in all required fields (Title, Subject, Level)');
            return;
          }
          
          console.log('Creating exam with data:', examForm.formData);
          
          const examData = {
            title: examForm.formData.title,
            description: examForm.formData.description || '',
            subjectId: examForm.formData.subjectId,
            levelId: examForm.formData.levelId,
            duration: parseInt(examForm.formData.examDuration) || 60, // Changed from examDuration to duration
            passingMarks: parseInt(examForm.formData.passingMarks) || 50,
            totalMarks: parseInt(examForm.formData.totalMarks) || 100,
            createdAt: new Date(),
            questions: []
          };
          
          console.log('Formatted exam data:', examData);
          
          await createExam(examData);
          
          examForm.resetForm();
          closeModal('exam');
          
          const examsData = await getAllExams();
          setExams(examsData);
          
          showSuccessNotification('Exam created successfully!');
          break;
          
        default:
          throw new Error('Invalid form type');
      }
    } catch (error) {
      console.error(`Error submitting ${type}:`, error);
      
      // Provide more specific error messages
      let errorMessage = `Failed to ${type}. Please try again.`;
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        errorMessage = `Server error: ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        // Something happened in setting up the request that triggered an Error
        errorMessage = error.message;
      }
      
      showErrorNotification(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Notification system
  const [notifications, setNotifications] = useState([]);
  
  // Notification helper
  const showNotification = useCallback((message, type = 'error') => {
    const id = Date.now();
    const notification = { id, message, type };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);
  
  const showSuccessNotification = useCallback((message) => {
    showNotification(message, 'success');
  }, [showNotification]);
  
  const showErrorNotification = useCallback((message) => {
    showNotification(message, 'error');
  }, [showNotification]);
  
  // Remove notification manually
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);
  
  // Delete handlers with confirmation
  const handleDelete = async (type, id, itemName) => {
    if (!window.confirm(`Are you sure you want to delete this ${itemName}?`)) {
      return;
    }
    
    try {
      switch (type) {
        case 'paper':
          await deletePastPaper(id);
          const papersData = await getAllPapers();
          setPapers(papersData);
          showSuccessNotification('Paper deleted successfully!');
          break;
          
        case 'subject':
          await deleteSubject(id);
          const subjectsData = await getAllSubjects();
          setSubjects(subjectsData);
          showSuccessNotification('Subject deleted successfully!');
          break;
          
        case 'video':
          await deleteVideoLesson(id);
          const videosData = await getAllVideos();
          setVideos(videosData);
          showSuccessNotification('Video deleted successfully!');
          break;
          
        case 'exam':
          await deleteExam(id);
          const examsData = await getAllExams();
          setExams(examsData);
          showSuccessNotification('Exam deleted successfully!');
          break;
          
        default:
          throw new Error('Invalid delete type');
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      showErrorNotification(`Failed to delete ${itemName}. Please try again.`);
    }
  };
  
  // Utility functions
  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Unknown Subject';
  };

  const formatDate = useCallback((dateValue) => {
    if (!dateValue) return 'N/A';
    
    // Handle Firestore Timestamp
    if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    
    // Handle string dates
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }, []);

  if (loading) {
    return (
      <div className="content-management">
        <div className="loading-container">
          <div className="loading-spinner">Loading content...</div>
        </div>
      </div>
    );
  }

  // Tab navigation with professional icons
  const tabConfig = [
    { id: 'papers', label: 'Past Papers', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10,9 9,9 8,9"/>
      </svg>
    )},
    { id: 'subjects', label: 'Subjects', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        <polyline points="8,7 16,7 16,11 8,11"/>
        <polyline points="8,13 16,13 16,17 8,17"/>
      </svg>
    )},
    { id: 'videos', label: 'Videos', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="23 7 16 12 23 17 23 7"/>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
      </svg>
    )},
    { id: 'exams', label: 'Exams', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    )}
  ];

  return (
    <div className="content-management">
      <div className="page-header">
        <h1>Content Management</h1>
        <p>Manage subjects, past papers, videos, and exams</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        {tabConfig.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            aria-label={`Show ${tab.label}`}
            aria-selected={activeTab === tab.id}
            role="tab"
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Notification Container */}
      <div className="notification-container">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`notification notification-${notification.type}`}
            role="alert"
            aria-live="polite"
          >
            <span className="notification-icon">
              {notification.type === 'success' ? '✓' : '⚠'}
            </span>
            <span className="notification-message">{notification.message}</span>
            <button
              className="notification-close"
              onClick={() => removeNotification(notification.id)}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'papers' && (
        <div className="tab-content" role="tabpanel" aria-labelledby="tab-papers">
          <div className="tab-header">
            <h2>Past Papers</h2>
            <div className="tab-actions">
              <button
                onClick={() => openModal('bulkUpload')}
                className="btn btn-secondary"
                aria-label="Upload multiple papers"
              >
                <span className="btn-icon">📁</span>
                Bulk Upload
              </button>
              <button
                onClick={() => openModal('upload')}
                className="btn btn-primary"
                aria-label="Upload new paper"
              >
                <span className="btn-icon">+</span>
                Upload Paper
              </button>
            </div>
          </div>

          {/* Papers Grid */}
          <div className="papers-grid">
            {papers.map((paper) => (
              <div key={paper.id} className="paper-button">
                <div className="paper-main">
                  <div className="paper-title">{paper.title}</div>
                  <div className="paper-meta">
                    <span className="paper-subject">{getSubjectName(paper.subjectId)}</span>
                    <span className="paper-year">{paper.year}</span>
                  </div>
                </div>
                <div className="paper-actions">
                  <a
                    href={paper.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-primary"
                    aria-label={`View ${paper.title}`}
                  >
                    View
                  </a>
                  <button 
                    onClick={() => handleDelete('paper', paper.id, 'paper')}
                    className="btn btn-sm btn-danger"
                    aria-label={`Delete ${paper.title}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {papers.length === 0 && (
            <div className="no-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              <h3>No past papers uploaded yet</h3>
              <p>Start by uploading your first past paper.</p>
              <button
                onClick={() => openModal('upload')}
                className="btn btn-primary"
                aria-label="Upload first paper"
              >
                <span className="btn-icon">+</span>
                Upload First Paper
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'subjects' && (
        <div className="tab-content" role="tabpanel" aria-labelledby="tab-subjects">
          <div className="tab-header">
            <h2>Subjects</h2>
            <button
              onClick={() => openModal('subject')}
              className="btn btn-primary"
              aria-label="Add new subject"
            >
              <span className="btn-icon">+</span>
              Add Subject
            </button>
          </div>

          <div className="subjects-grid">
            {subjects.map((subject) => (
              <div key={subject.id} className="subject-card">
                <div className="subject-header">
                  <h3>{subject.name}</h3>
                  <span className="subject-level">{subject.levelId?.replace('grade', 'Grade ').replace('form', 'Form ')}</span>
                </div>
                
                <div className="subject-actions">
                  <button className="btn btn-sm btn-secondary" aria-label={`Edit ${subject.name}`}>Edit</button>
                  <button 
                    onClick={() => handleDelete('subject', subject.id, 'subject')}
                    className="btn btn-sm btn-danger"
                    aria-label={`Delete ${subject.name}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {subjects.length === 0 && (
            <div className="no-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                <polyline points="8,7 16,7 16,11 8,11"/>
                <polyline points="8,13 16,13 16,17 8,17"/>
              </svg>
              <h3>No subjects created yet</h3>
              <p>Create your first subject to organize content.</p>
              <button
                onClick={() => openModal('subject')}
                className="btn btn-primary"
                aria-label="Add first subject"
              >
                <span className="btn-icon">+</span>
                Add First Subject
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'videos' && (
        <div className="tab-content" role="tabpanel" aria-labelledby="tab-videos">
          <div className="tab-header">
            <h2>Video Lessons</h2>
            <button
              onClick={() => openModal('video')}
              className="btn btn-primary"
              aria-label="Add new video"
            >
              <span className="btn-icon">+</span>
              Add Video
            </button>
          </div>

          <div className="videos-grid">
            {videos.map((video) => (
              <div key={video.id} className="video-card">
                <div className="video-header">
                  <h3>{video.title}</h3>
                  <span className="video-duration">{video.duration}</span>
                </div>
                
                <div className="video-info">
                  <p><strong>Subject:</strong> {getSubjectName(video.subjectId)}</p>
                  <p><strong>Level:</strong> {video.levelId?.replace('grade', 'Grade ').replace('form', 'Form ')}</p>
                  <p><strong>Description:</strong> {video.description || 'No description'}</p>
                  <p><strong>Created:</strong> {formatDate(video.createdAt)}</p>
                </div>

                <div className="video-actions">
                  <a
                    href={video.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-secondary"
                    aria-label={`Watch ${video.title}`}
                  >
                    Watch
                  </a>
                  <button 
                    onClick={() => handleDelete('video', video.id, 'video')}
                    className="btn btn-sm btn-danger"
                    aria-label={`Delete ${video.title}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {videos.length === 0 && (
            <div className="no-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="23 7 16 12 23 17 23 7"/>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
              </svg>
              <h3>No videos added yet</h3>
              <p>Add your first video lesson.</p>
              <button
                onClick={() => openModal('video')}
                className="btn btn-primary"
                aria-label="Add first video"
              >
                <span className="btn-icon">+</span>
                Add First Video
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'exams' && (
        <div className="tab-content" role="tabpanel" aria-labelledby="tab-exams">
          <div className="tab-header">
            <h2>Exams</h2>
            <div className="tab-actions">
              <button
                onClick={() => openModal('bulkQuestions')}
                className="btn btn-secondary"
                aria-label="Upload multiple questions"
              >
                <span className="btn-icon">📝</span>
                Bulk Questions
              </button>
              <button
                onClick={() => openModal('exam')}
                className="btn btn-primary"
                aria-label="Create new exam"
              >
                <span className="btn-icon">+</span>
                Create Exam
              </button>
            </div>
          </div>

          <div className="exams-grid">
            {exams.map((exam) => (
              <div key={exam.id} className="exam-button">
                <div className="exam-main">
                  <div className="exam-title">{exam.title}</div>
                  <div className="exam-meta">
                    <span className="exam-questions">{exam.questions?.length || 0} questions</span>
                    <span className="exam-duration">{exam.duration} min</span>
                  </div>
                </div>
                <div className="exam-actions">
                  <button 
                    onClick={() => openModal('editQuestions', exam)}
                    className="btn btn-sm btn-secondary" 
                    aria-label={`Edit questions for ${exam.title}`}
                  >
                    Edit Questions
                  </button>
                  <button 
                    className="btn btn-sm btn-primary"
                    aria-label={`View details for ${exam.title}`}
                  >
                    View Details
                  </button>
                  <button 
                    onClick={() => handleDelete('exam', exam.id, 'exam')}
                    className="btn btn-sm btn-danger"
                    aria-label={`Delete ${exam.title}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {exams.length === 0 && (
            <div className="no-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <h3>No exams created yet</h3>
              <p>Create your first exam.</p>
              <button
                onClick={() => openModal('exam')}
                className="btn btn-primary"
                aria-label="Create first exam"
              >
                <span className="btn-icon">+</span>
                Create First Exam
              </button>
            </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {modals.upload && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Upload Past Paper</h3>
              <button
                onClick={() => closeModal('upload')}
                className="modal-close"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => handleSubmit(e, 'paper')} className="modal-content">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={paperForm.formData.title}
                    onChange={(e) => paperForm.updateField('title', e.target.value)}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Paper Type *</label>
                  <select
                    value={paperForm.formData.paperType}
                    onChange={(e) => paperForm.updateField('paperType', e.target.value)}
                    className="form-control"
                    required
                  >
                    {PAPER_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={paperForm.formData.description}
                  onChange={(e) => paperForm.updateField('description', e.target.value)}
                  className="form-control"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Subject *</label>
                  <select
                    value={paperForm.formData.subjectId}
                    onChange={(e) => paperForm.updateField('subjectId', e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Level *</label>
                  <select
                    value={paperForm.formData.levelId}
                    onChange={(e) => paperForm.updateField('levelId', e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Level</option>
                    {LEVELS.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Year *</label>
                  <input
                    type="number"
                    value={paperForm.formData.year}
                    onChange={(e) => paperForm.updateField('year', e.target.value)}
                    className="form-control"
                    min="2010"
                    max={new Date().getFullYear()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Term</label>
                  <select
                    value={paperForm.formData.term}
                    onChange={(e) => paperForm.updateField('term', e.target.value)}
                    className="form-control"
                  >
                    <option value="">Select Term</option>
                    {TERMS.map(term => (
                      <option key={term.value} value={term.value}>{term.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>PDF File *</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="form-control"
                  required
                />
                {selectedFile && (
                  <small>Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})</small>
                )}
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => closeModal('upload')}
                  className="btn btn-secondary"
                  disabled={submitting || uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || uploading}
                >
                  {submitting || uploading ? 'Uploading...' : 'Upload Paper'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subject Modal */}
      {modals.subject && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Subject</h3>
              <button
                onClick={() => closeModal('subject')}
                className="modal-close"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => handleSubmit(e, 'subject')} className="modal-content">
              <div className="form-group">
                <label>Subject Name *</label>
                <input
                  type="text"
                  value={subjectForm.formData.title}
                  onChange={(e) => subjectForm.updateField('title', e.target.value)}
                  className="form-control"
                  placeholder="e.g., Mathematics"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={subjectForm.formData.description}
                  onChange={(e) => subjectForm.updateField('description', e.target.value)}
                  className="form-control"
                  rows={3}
                  placeholder="Brief description of the subject"
                />
              </div>

              <div className="form-group">
                <label>Level *</label>
                <select
                  value={subjectForm.formData.levelId}
                  onChange={(e) => subjectForm.updateField('levelId', e.target.value)}
                  className="form-control"
                  required
                >
                  <option value="">Select Level</option>
                  {LEVELS.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Subject Code *</label>
                <input
                  type="text"
                  value={subjectForm.formData.subjectId}
                  onChange={(e) => subjectForm.updateField('subjectId', e.target.value)}
                  className="form-control"
                  placeholder="e.g., mathematics"
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => closeModal('subject')}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Add Subject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {modals.video && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Video Lesson</h3>
              <button
                onClick={() => closeModal('video')}
                className="modal-close"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => handleSubmit(e, 'video')} className="modal-content">
              <div className="form-group">
                <label>Video Title *</label>
                <input
                  type="text"
                  value={videoForm.formData.title}
                  onChange={(e) => videoForm.updateField('title', e.target.value)}
                  className="form-control"
                  placeholder="e.g., Algebra Basics"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={videoForm.formData.description}
                  onChange={(e) => videoForm.updateField('description', e.target.value)}
                  className="form-control"
                  rows={3}
                  placeholder="Brief description of the video content"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Subject *</label>
                  <select
                    value={videoForm.formData.subjectId}
                    onChange={(e) => videoForm.updateField('subjectId', e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Level *</label>
                  <select
                    value={videoForm.formData.levelId}
                    onChange={(e) => videoForm.updateField('levelId', e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Level</option>
                    {LEVELS.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>YouTube Video URL *</label>
                <input
                  type="url"
                  value={videoForm.formData.videoUrl}
                  onChange={(e) => videoForm.updateField('videoUrl', e.target.value)}
                  className="form-control"
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Duration</label>
                <input
                  type="text"
                  value={videoForm.formData.duration}
                  onChange={(e) => videoForm.updateField('duration', e.target.value)}
                  className="form-control"
                  placeholder="e.g., 45 minutes"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => closeModal('video')}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Exam Modal */}
      {modals.exam && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create Exam</h3>
              <button
                onClick={() => closeModal('exam')}
                className="modal-close"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => handleSubmit(e, 'exam')} className="modal-content">
              <div className="form-group">
                <label>Exam Title *</label>
                <input
                  type="text"
                  value={examForm.formData.title}
                  onChange={(e) => examForm.updateField('title', e.target.value)}
                  className="form-control"
                  placeholder="e.g., Grade 7 Mathematics Final"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={examForm.formData.description}
                  onChange={(e) => examForm.updateField('description', e.target.value)}
                  className="form-control"
                  rows={3}
                  placeholder="Exam instructions and details"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Subject *</label>
                  <select
                    value={examForm.formData.subjectId}
                    onChange={(e) => examForm.updateField('subjectId', e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Level *</label>
                  <select
                    value={examForm.formData.levelId}
                    onChange={(e) => examForm.updateField('levelId', e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Level</option>
                    {LEVELS.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Duration (minutes) *</label>
                  <input
                    type="number"
                    value={examForm.formData.examDuration}
                    onChange={(e) => examForm.updateField('examDuration', parseInt(e.target.value))}
                    className="form-control"
                    min="30"
                    max="180"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Total Marks *</label>
                  <input
                    type="number"
                    value={examForm.formData.totalMarks}
                    onChange={(e) => examForm.updateField('totalMarks', parseInt(e.target.value))}
                    className="form-control"
                    min="1"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Passing Marks *</label>
                <input
                  type="number"
                  value={examForm.formData.passingMarks}
                  onChange={(e) => examForm.updateField('passingMarks', parseInt(e.target.value))}
                  className="form-control"
                  min="1"
                  max={examForm.formData.totalMarks}
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => closeModal('exam')}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Create Exam'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Questions Modal */}
      {modals.editQuestions && selectedExam && (
        <div className="modal-overlay" onClick={() => closeModal('editQuestions')}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Questions: {selectedExam.title}</h3>
              <button
                onClick={() => closeModal('editQuestions')}
                className="modal-close"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="exam-info-summary">
                <p><strong>Subject:</strong> {getSubjectName(selectedExam.subjectId)}</p>
                <p><strong>Level:</strong> {selectedExam.levelId?.replace('grade', 'Grade ').replace('form', 'Form ')}</p>
                <p><strong>Duration:</strong> {selectedExam.duration} minutes</p>
                <p><strong>Total Questions:</strong> {selectedExam.questions?.length || 0}</p>
              </div>
              
              <div className="questions-section">
                <div className="questions-header">
                  <h4>Questions ({selectedExam.questions?.length || 0})</h4>
                  <div className="questions-actions">
                    <button
                      onClick={() => openModal('bulkQuestions')}
                      className="btn btn-sm btn-primary"
                    >
                      <span className="btn-icon">�</span>
                      Bulk Upload Questions
                    </button>
                  </div>
                </div>
                
                {selectedExam.questions && selectedExam.questions.length > 0 ? (
                  <div className="questions-list">
                    {selectedExam.questions.map((question, index) => (
                      <div key={question.id || index} className="question-item">
                        <div className="question-header">
                          <span className="question-number">Question {index + 1}</span>
                          <div className="question-meta">
                            <span className="question-type">{question.type || 'multiple_choice'}</span>
                            <span className="question-marks">{question.marks || 1} marks</span>
                          </div>
                        </div>
                        
                        <div className="question-content">
                          {question.imageUrl && (
                            <div className="question-image">
                              <img 
                                src={question.imageUrl} 
                                alt="Question image" 
                                className="question-img"
                              />
                            </div>
                          )}
                          <p className="question-text">{question.question}</p>
                          
                          {question.type === 'multiple_choice' && question.options && (
                            <div className="question-options">
                              {question.options.map((option, optionIndex) => (
                                <div 
                                  key={optionIndex} 
                                  className={`option ${optionIndex === question.correctAnswer ? 'correct' : ''}`}
                                >
                                  <span className="option-label">{String.fromCharCode(65 + optionIndex)}.</span>
                                  <span className="option-text">{option}</span>
                                  {optionIndex === question.correctAnswer && (
                                    <span className="correct-indicator">✓</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {question.type === 'true_false' && (
                            <div className="true-false-answer">
                              <span className="answer-label">Correct Answer:</span>
                              <span className="answer-value">
                                {question.correctAnswer ? 'True' : 'False'}
                              </span>
                            </div>
                          )}
                          
                          {question.type === 'short_answer' && (
                            <div className="short-answer-answer">
                              <span className="answer-label">Correct Answer:</span>
                              <div className="answer-value short-answer-text">
                                {question.correctAnswer || 'No answer specified'}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="question-actions">
                          <button 
                            onClick={() => handleEditQuestion(index)}
                            className="btn btn-sm btn-secondary"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteQuestion(index)}
                            className="btn btn-sm btn-danger"
                          >
                            Delete
                          </button>
                        </div>
                        
                        {/* Inline Edit Form */}
                        {editingQuestionIndex === index && (
                          <div className="question-edit-form">
                            <div className="edit-form-header">
                              <h5>Edit Question {index + 1}</h5>
                            </div>
                            
                            <div className="edit-form-content">
                              <div className="form-group">
                                <label>Question *</label>
                                <textarea
                                  value={questionForm.question}
                                  onChange={(e) => setQuestionForm({...questionForm, question: e.target.value})}
                                  className="form-control"
                                  rows={3}
                                  required
                                />
                              </div>

                              <div className="form-row">
                                <div className="form-group">
                                  <label>Type</label>
                                  <select
                                    value={questionForm.type}
                                    onChange={(e) => setQuestionForm({...questionForm, type: e.target.value})}
                                    className="form-control"
                                  >
                                    <option value="multiple_choice">Multiple Choice</option>
                                    <option value="true_false">True/False</option>
                                    <option value="short_answer">Short Answer</option>
                                  </select>
                                </div>

                                <div className="form-group">
                                  <label>Marks</label>
                                  <input
                                    type="number"
                                    value={questionForm.marks}
                                    onChange={(e) => setQuestionForm({...questionForm, marks: parseInt(e.target.value)})}
                                    className="form-control"
                                    min="1"
                                    required
                                  />
                                </div>
                              </div>

                              {questionForm.type === 'multiple_choice' && (
                                <div className="options-group">
                                  <label>Options</label>
                                  {questionForm.options.map((option, optionIndex) => (
                                    <div key={optionIndex} className="option-input">
                                      <input
                                        type="text"
                                        value={option}
                                        onChange={(e) => {
                                          const newOptions = [...questionForm.options];
                                          newOptions[optionIndex] = e.target.value;
                                          setQuestionForm({...questionForm, options: newOptions});
                                        }}
                                        className="form-control"
                                        placeholder={`Option ${optionIndex + 1}`}
                                        required
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}

                              {questionForm.type === 'multiple_choice' && (
                                <div className="form-group">
                                  <label>Correct Answer</label>
                                  <select
                                    value={questionForm.correctAnswer}
                                    onChange={(e) => setQuestionForm({...questionForm, correctAnswer: parseInt(e.target.value)})}
                                    className="form-control"
                                    required
                                  >
                                    <option value={0}>Option 1</option>
                                    <option value={1}>Option 2</option>
                                    <option value={2}>Option 3</option>
                                    <option value={3}>Option 4</option>
                                  </select>
                                </div>
                              )}

                              {questionForm.type === 'true_false' && (
                                <div className="form-group">
                                  <label>Correct Answer</label>
                                  <select
                                    value={questionForm.correctAnswer}
                                    onChange={(e) => setQuestionForm({...questionForm, correctAnswer: e.target.value === 'true'})}
                                    className="form-control"
                                    required
                                  >
                                    <option value="true">True</option>
                                    <option value="false">False</option>
                                  </select>
                                </div>
                              )}

                              {questionForm.type === 'short_answer' && (
                                <div className="form-group">
                                  <label>Correct Answer</label>
                                  <input
                                    type="text"
                                    value={questionForm.correctAnswer}
                                    onChange={(e) => setQuestionForm({...questionForm, correctAnswer: e.target.value})}
                                    className="form-control"
                                    placeholder="Enter the correct answer"
                                    required
                                  />
                                </div>
                              )}

                              <div className="form-group">
                                <label>Question Image (Optional)</label>
                                <div className="image-upload-section">
                                  {questionForm.imageUrl && (
                                    <div className="image-preview">
                                      <img 
                                        src={questionForm.imageUrl} 
                                        alt="Question preview" 
                                        className="preview-img"
                                      />
                                      <button
                                        type="button"
                                        onClick={removeQuestionImage}
                                        className="btn btn-sm btn-danger remove-image-btn"
                                      >
                                        Remove Image
                                      </button>
                                    </div>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleQuestionImageSelect}
                                    className="form-control"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="edit-form-actions">
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="btn btn-secondary"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveEditedQuestion}
                                className="btn btn-primary"
                                disabled={uploading}
                              >
                                {uploading ? 'Saving...' : 'Save Changes'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-questions">
                    <div className="no-questions-icon">❓</div>
                    <h4>No Questions Added</h4>
                    <p>This exam doesn't have any questions yet. Add questions to make it ready for students.</p>
                    <button className="btn btn-primary">
                      <span className="btn-icon">+</span>
                      Add First Question
                    </button>
                  </div>
                )}
              </div>
              
              <div className="add-question-section">
                <h4>Add New Question</h4>
                
                <div className="form-group">
                  <label>Question *</label>
                  <textarea
                    value={questionForm.question}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, question: e.target.value }))}
                    className="form-control"
                    rows={3}
                    placeholder="Enter your question here..."
                  />
                </div>
                
                {/* Image Upload Section */}
                <div className="form-group">
                  <label>Question Image (Optional)</label>
                  <div className="image-upload-section">
                    {questionForm.imageUrl ? (
                      <div className="image-preview">
                        <img 
                          src={questionForm.imageUrl} 
                          alt="Question preview" 
                          className="preview-image"
                        />
                        <button
                          type="button"
                          onClick={removeQuestionImage}
                          className="btn btn-sm btn-danger remove-image-btn"
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-area">
                        <input
                          type="file"
                          id="question-image"
                          accept="image/*"
                          onChange={handleQuestionImageSelect}
                          className="image-input"
                        />
                        <label htmlFor="question-image" className="image-upload-label">
                          <div className="upload-icon">📷</div>
                          <div className="upload-text">
                            <p>Click to upload image</p>
                            <small>PNG, JPG, GIF up to 5MB</small>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Question Type</label>
                    <select 
                      value={questionForm.type}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, type: e.target.value }))}
                      className="form-control"
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="true_false">True/False</option>
                      <option value="short_answer">Short Answer</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Marks</label>
                    <input
                      type="number"
                      value={questionForm.marks}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, marks: parseInt(e.target.value) || 1 }))}
                      className="form-control"
                      min="1"
                    />
                  </div>
                </div>
                
                {questionForm.type === 'multiple_choice' && (
                  <>
                    <div className="form-group">
                      <label>Options</label>
                      <div className="options-inputs">
                        {questionForm.options.map((option, index) => (
                          <input
                            key={index}
                            type="text"
                            value={option}
                            onChange={(e) => {
                              const newOptions = [...questionForm.options];
                              newOptions[index] = e.target.value;
                              setQuestionForm(prev => ({ ...prev, options: newOptions }));
                            }}
                            className="form-control"
                            placeholder={`Option ${String.fromCharCode(65 + index)}`}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Correct Answer</label>
                      <select 
                        value={questionForm.correctAnswer}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, correctAnswer: parseInt(e.target.value) }))}
                        className="form-control"
                      >
                        {questionForm.options.map((option, index) => (
                          <option key={index} value={index}>
                            Option {String.fromCharCode(65 + index)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                
                {questionForm.type === 'true_false' && (
                  <div className="form-group">
                    <label>Correct Answer</label>
                    <select 
                      value={questionForm.correctAnswer}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, correctAnswer: e.target.value === 'true' }))}
                      className="form-control"
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                  </div>
                )}
                
                {questionForm.type === 'short_answer' && (
                  <div className="form-group">
                    <label>Correct Answer *</label>
                    <textarea
                      value={questionForm.correctAnswer || ''}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, correctAnswer: e.target.value }))}
                      className="form-control"
                      rows={2}
                      placeholder="Enter the correct answer for this short answer question..."
                    />
                    <small className="form-help-text">
                      This is the answer that will be used to evaluate student responses.
                    </small>
                  </div>
                )}
                
                <div className="question-form-actions">
                  <button
                    type="button"
                    onClick={resetQuestionForm}
                    className="btn btn-secondary"
                  >
                    Clear Form
                  </button>
                  <button
                    type="button"
                    onClick={handleAddQuestion}
                    className="btn btn-primary"
                    disabled={uploading}
                  >
                    {uploading ? 'Adding...' : 'Add Question'}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button
                onClick={() => closeModal('editQuestions')}
                className="btn btn-secondary"
                disabled={submitting}
              >
                Close
              </button>
              <button 
                onClick={handleSaveExamChanges}
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal - Papers */}
      {modals.bulkUpload && (
        <BulkPaperUpload
          onClose={() => closeModal('bulkUpload')}
          onUploadComplete={handleBulkUploadComplete}
        />
      )}

      {/* Bulk Questions Modal - Exams */}
      {modals.bulkQuestions && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>Bulk Upload Questions</h3>
              <button
                onClick={() => closeModal('bulkQuestions')}
                className="modal-close"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            <BulkQuestionUpload
              onQuestionsAdded={handleBulkQuestionsAdded}
              onClose={() => closeModal('bulkQuestions')}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
